import { FC, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { userSelectors } from '@/redux/selectors';
import { userActions } from '@/redux/actions';
import { useInputText } from '@/hooks';
import { Validation } from '@/models';
import { ChangeUserPasswordRequest } from '@/api';
import { COMMON_CONTAINER_ID, PASSWORD_REGEX } from '@/config';

import useDevice from '@/hooks/useDevice';
import { AlertBox, Input, InputGroup, Button } from '@/components';
import { ModalInfo, ModalInfoProps } from '../ModalInfo';

interface ChangePasswordProps extends ModalInfoProps {}

const ChangePassword: FC<ChangePasswordProps> = (props) => {
  const { isOpen, onClose } = props;
  const { error, loading } = useAppSelector(userSelectors.getUserSlice);
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { isMobile } = useDevice();

  useEffect(() => {
    dispatch(userActions.setError(null));
  }, []);

  const PASSWORD_VALIDATIONS: Validation[] = useMemo(
    () => [
      {
        name: 'required',
        value: true,
        message: t('translation.field_is_required'),
      },
      {
        name: 'regex',
        value: PASSWORD_REGEX,
        message: t('translation.reset_pwd_error_hint_length'),
      },
    ],
    [t],
  );

  const passwordField = useInputText('', PASSWORD_VALIDATIONS);

  const PASSWORD_CONFIRM_VALIDATIONS: Validation[] = useMemo(() => {
    return [
      {
        name: 'required',
        value: true,
        message: t('translation.field_is_required'),
      },
      {
        name: 'equal',
        value: passwordField.value,
        message: t('translation.password_mismatch'),
      },
    ];
  }, [passwordField.value, t]);

  const passwordConfirmField = useInputText('', PASSWORD_CONFIRM_VALIDATIONS);

  useEffect(() => {
    if (
      !error &&
      !loading &&
      passwordField.isValid &&
      passwordField.isDirty &&
      passwordConfirmField.isValid &&
      passwordConfirmField.isDirty
    ) {
      toast.success(t('translation.toast_password_updated'), {
        containerId: COMMON_CONTAINER_ID,
      });
      onClose();
    }
  }, [error, loading]);

  useEffect(() => {
    if (!isOpen) {
      passwordField.onReset();
      passwordConfirmField.onReset();
    }
  }, [isOpen]);

  const submitHandler = () => {
    if (!loading) {
      if (passwordField.isValid && passwordConfirmField.isValid) {
        const payload: ChangeUserPasswordRequest = {
          password: passwordField.value,
        };

        dispatch(userActions.changeUserPassword(payload));
      } else {
        if (!passwordField.isValid) {
          passwordField.onBlur();
        }

        if (!passwordConfirmField.isValid) {
          passwordConfirmField.onBlur();
        }
      }
    }
  };

  const cancelHandler = () => {
    onClose();
    if (!!error) {
      dispatch(userActions.setError(null));
    }
  };

  const actionButtonsWidth = isMobile ? 120 : 175;

  return (
    <ModalInfo
      isOpen={isOpen}
      title={t('translation.change_pwd_title')}
      actions={
        <>
          <Button
            type="primary"
            corner="rect"
            minWidth={actionButtonsWidth}
            disabled={loading}
            isLoading={loading}
            onClick={submitHandler}
          >
            {t('translation.change_btn')}
          </Button>
          <Button
            type="secondary"
            corner="rect"
            minWidth={actionButtonsWidth}
            disabled={loading}
            onClick={cancelHandler}
          >
            {t('translation.cancel_btn')}
          </Button>
        </>
      }
      onClose={onClose}
    >
      {!!error && <AlertBox type="danger">{{ description: error }}</AlertBox>}

      <InputGroup label={t('translation.new_pwd_label')}>
        <Input
          id="new-password"
          name="new-password"
          type="password"
          value={passwordField.value}
          onChange={passwordField.onChange}
          onBlur={passwordField.onBlur}
          placeholder={t('translation.new_password')!}
          disabled={loading}
          isError={passwordField.isDirty && !passwordField.isValid}
          required
        />
        {passwordField.isDirty && !passwordField.isValid && (
          <AlertBox type="danger" small>
            {{ description: passwordField.errors[0] }}
          </AlertBox>
        )}
      </InputGroup>

      <InputGroup label={t('translation.confirm_new_pwd_label')}>
        <Input
          id="confirm-password"
          name="confirm-password"
          type="password"
          value={passwordConfirmField.value}
          onChange={passwordConfirmField.onChange}
          onBlur={passwordConfirmField.onBlur}
          placeholder={t('translation.confirm_new_password')!}
          disabled={loading}
          isError={
            passwordConfirmField.isDirty && !passwordConfirmField.isValid
          }
          required
        />
        {passwordConfirmField.isDirty && !passwordConfirmField.isValid && (
          <AlertBox type="danger" small>
            {{
              description: passwordConfirmField.errors[0],
            }}
          </AlertBox>
        )}
      </InputGroup>
    </ModalInfo>
  );
};

export default ChangePassword;
